import { useMutation, useQuery } from '@tanstack/react-query';
import { call } from '@/lib/api';
import { queryClient } from '@/lib/query';
import type { Contact } from '@/models/contacts';
import type { Lead } from '@/models/leads';

export type ReferringAgent = {
  id: number;
  name: string;
  leadCount?: number;
  contactCount?: number;
  createdAt: string;
  updatedAt: string;
};

export type ReferringAgentListParams = {
  term?: string;
  page?: number;
  pageSize?: number;
};

export type ReferringAgentListResponse = {
  agents: ReferringAgent[];
  total: number;
  page: number;
  pageSize: number;
};

export type ReferringAgentDetailsResponse = {
  agent: ReferringAgent;
  leads: Lead[];
  contacts: Contact[];
  mondayItemIds: number[];
};

export const referringAgentKeys = {
  all: ['referring-agents'] as const,
  lists: () => [...referringAgentKeys.all, 'list'] as const,
  list: (params: ReferringAgentListParams) =>
    [...referringAgentKeys.lists(), params] as const,
  details: () => [...referringAgentKeys.all, 'detail'] as const,
  detail: (id: number) =>
    [...referringAgentKeys.details(), Number(id)] as const,
};

const callGet = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { method: 'GET', json }).then((r) => r.json());

const callPost = <T>(endpoint: string, json?: any): Promise<T> =>
  call<T>(endpoint, { json }).then((r) => r.json());

export function useReferringAgents(
  term = '',
  page?: number,
  pageSize?: number,
) {
  const params: ReferringAgentListParams = { term, page, pageSize };
  return useQuery({
    queryKey: referringAgentKeys.list(params),
    queryFn: () =>
      callGet<ReferringAgentListResponse>('referring-agents/list', params),
  });
}

export function referringAgentOptions(
  agents: ReferringAgent[] | undefined,
  currentId: number | null | undefined,
  currentName: string | null | undefined,
): { value: string; label: string }[] {
  const options = (agents ?? []).map((a) => ({
    value: String(a.id),
    label: a.name,
  }));
  if (
    currentId != null &&
    !options.some((o) => o.value === String(currentId))
  ) {
    options.push({
      value: String(currentId),
      label: currentName ?? `Agent #${currentId}`,
    });
  }
  return options;
}

export function useReferringAgentDetails(id: number | null | undefined) {
  return useQuery({
    enabled: id != null,
    queryKey: referringAgentKeys.detail(id ?? 0),
    queryFn: () =>
      callGet<ReferringAgentDetailsResponse>('referring-agents/details', {
        id,
      }),
  });
}

const invalidateAll = () =>
  queryClient.invalidateQueries({ queryKey: referringAgentKeys.all });

export function useCreateReferringAgent() {
  return useMutation({
    mutationFn: (body: { name: string }) =>
      callPost<{ agent: ReferringAgent }>('referring-agents/create', body),
    onSuccess: invalidateAll,
  });
}

export function useUpdateReferringAgent() {
  return useMutation({
    mutationFn: (body: { id: number; name: string }) =>
      callPost<{ agent: ReferringAgent }>('referring-agents/update', body),
    onSuccess: (_d, vars) => {
      invalidateAll();
      queryClient.invalidateQueries({
        queryKey: referringAgentKeys.detail(vars.id),
      });
    },
  });
}

export function useDeleteReferringAgent() {
  return useMutation({
    mutationFn: (id: number) =>
      callPost<void>('referring-agents/delete', { id }),
    onSuccess: invalidateAll,
  });
}
